import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useSafetyVisible } from '../hooks';
import { LineDivider } from './LineDivider';
import { SafetyAlertRow } from './SafetyAlertRow';
import { useSafetyAlertDialog } from './useSafetyAlertDialog';
import { useSafetySource } from './useRailSource';
import styles from './leftRail.module.css';

/** Initial row-height guess — refined per row via `measureElement`. */
const ROW_ESTIMATE = 56;

type ISafetyEventsTabProps = {
  /** The LeftRail scroll container — the events log windows against it. */
  scrollRef: RefObject<HTMLDivElement | null>;
};

/**
 * Full safety view. The EVENTS LOG can hold thousands of closed events, so
 * it is virtualized (windowed) against the LeftRail scroll container — only
 * the rows on screen exist in the DOM. The LIVE section is small and
 * rendered as-is.
 */
export function SafetyEventsTab({ scrollRef }: ISafetyEventsTabProps) {
  const { t } = useTranslation();
  // Live, or the replayed day's up to the playhead (no LIVE section: nothing in the past is open now).
  const { active, closed, replaying } = useSafetySource();
  const allowed = useSafetyVisible();
  const { openAlert, alertDialog } = useSafetyAlertDialog();

  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // The events log starts partway down the scroll container (below the
  // title, LIVE section and divider). Feed that offset to the virtualizer
  // so windowed rows line up with the real scroll position.
  useLayoutEffect(() => {
    if (listRef.current) setScrollMargin(listRef.current.offsetTop);
  }, [active.length, closed.length]);

  // eslint-disable-next-line react-hooks/incompatible-library -- @tanstack/react-virtual's documented API; its return is intentionally not memoizable
  const virtualizer = useVirtualizer({
    count: closed.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_ESTIMATE,
    overscan: 12,
    scrollMargin,
  });

  if (!allowed) {
    return (
      <div className={styles.list}>
        <div className={styles.safetyEmpty}>
          {t('monitor.safety.notPermitted')}
        </div>
      </div>
    );
  }

  if (active.length === 0 && closed.length === 0) {
    return (
      <div className={styles.list}>
        <div className={styles.safetyEmpty}>{t('monitor.safety.empty')}</div>
      </div>
    );
  }

  return (
    <div className={styles.safetyLog}>
      <h3 className={styles.safetyLogTitle}>
        {t('monitor.safety.log')}{' '}
        <span className={styles.sectionCount}>
          ({active.length + closed.length})
        </span>
      </h3>

      {!replaying && (
        <>
          <LineDivider text={t('monitor.safety.live')} />
          {active.length > 0 ? (
            <div className={styles.safetyList}>
              {active.map((row) => (
                <SafetyAlertRow key={row.id} row={row} onOpen={openAlert} />
              ))}
            </div>
          ) : (
            <div className={styles.safetyEmptyLive}>
              {t('monitor.safety.noActiveAlerts')}
            </div>
          )}
        </>
      )}

      {closed.length > 0 && (
        <>
          <LineDivider text={t('monitor.safety.eventsLog')} />
          <div
            ref={listRef}
            className={styles.safetyVirtualList}
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((vItem) => {
              const row = closed[vItem.index]!;
              return (
                <div
                  key={row.id}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  className={styles.safetyVirtualRow}
                  style={{
                    transform: `translateY(${vItem.start - scrollMargin}px)`,
                  }}
                >
                  <SafetyAlertRow row={row} onOpen={openAlert} />
                </div>
              );
            })}
          </div>
        </>
      )}
      {alertDialog}
    </div>
  );
}
